import { InstagramOverlay } from '@/ui'
import { Jobibot, SubtleLoader } from '@/ui'
import { useImageWithAspect } from '@/ui'
import { CampaignModel, ConformedCampaignFeedAd, ConformedCampaignStoryAd } from '@/ui'
import { ChevronLeftIcon, ChevronRightIcon, EditIcon, ExternalLinkIcon } from '@/ui'
import { CampaignOverviewViewModel } from '@/ui'
import { useTranslation } from 'react-i18next'

const previewModes = ['feed', 'story'] as const
export type PreviewMode = typeof previewModes[number]

export type PreviewPanelProps = {
	previewMode: PreviewMode
	onPrevious: () => void
	onNext: () => void
	campaign: CampaignModel
	feedAd?: ConformedCampaignFeedAd
	storyAd?: ConformedCampaignStoryAd
	formPreviewLinkRenderer?: CampaignOverviewViewModel['renderers']['formPreviewLink']
	formEditorLinkRenderer?: CampaignOverviewViewModel['renderers']['formEditorLink']
	reachOverride?: { url: string }
	isLoading: boolean
}

export function PreviewPanel({
	previewMode,
	onPrevious,
	onNext,
	campaign,
	feedAd,
	storyAd,
	formPreviewLinkRenderer,
	formEditorLinkRenderer,
	reachOverride,
	isLoading,
}: PreviewPanelProps) {
	const { t } = useTranslation('common')

	return (
		<div className="flex flex-col">
			{/* Preview Mode Switcher */}
			<div className="flex items-center justify-between mb-4">
				<button
					type="button"
					onClick={onPrevious}
					className="cursor-pointer flex items-center gap-2 bg-purple-100 rounded-full px-4 py-2"
				>
					<div className="text-purple-600 hover:text-purple-800 transition-colors">
						<ChevronLeftIcon />
					</div>
					<div className="text-purple-700 font-medium text-sm min-w-[60px] text-center">
						{previewMode === 'feed' ? t('overview.preview.feedAd') : t('overview.preview.story')}
					</div>
					<div className="text-purple-600 hover:text-purple-800 transition-colors">
						<ChevronRightIcon />
					</div>
				</button>
				{(formEditorLinkRenderer || formPreviewLinkRenderer) && (
					<div className="flex items-center gap-2">
						{formEditorLinkRenderer && formEditorLinkRenderer({
							campaignId: campaign.id,
							children: (
								<span className="flex items-center gap-2 text-purple-600 border border-purple-200 rounded-full px-4 py-2 hover:bg-purple-50 transition-colors">
									<EditIcon />
									<span className="text-sm font-medium">{t('overview.preview.editForm')}</span>
								</span>
							),
						})}
						{formPreviewLinkRenderer && formPreviewLinkRenderer({
							campaignId: campaign.id,
							children: (
								<span className="flex items-center gap-2 text-purple-600 border border-purple-200 rounded-full px-4 py-2 hover:bg-purple-50 transition-colors">
									<span className="text-sm font-medium">{t('overview.preview.preview')}</span>
									<ExternalLinkIcon />
								</span>
							),
						})}
					</div>
				)}
			</div>

			{/* Ad Preview */}
			{previewMode === 'feed'
				? (
					<FeedAdPreview
						campaign={campaign}
						feedAd={feedAd}
						reachOverride={reachOverride}
						isLoading={isLoading}
					/>
				)
				: <StoryAdPreview campaign={campaign} storyAd={storyAd} isLoading={isLoading} />}
		</div>
	)
}

export type FeedAdPreviewProps = {
	campaign: CampaignModel
	feedAd?: ConformedCampaignFeedAd
	reachOverride?: { url: string }
	isLoading: boolean
}

export function FeedAdPreview({ campaign, feedAd, reachOverride, isLoading }: FeedAdPreviewProps) {
	const { t } = useTranslation('common')
	const primaryText = feedAd?.primaryText ?? ''
	const headlineText = feedAd?.headlineText ?? ''
	const descriptionText = feedAd?.descriptionText ?? ''
	const metaCtaText = feedAd?.metaCtaText ?? ''

	// The baked image from the campaign
	const bakedImage = campaign.image

	// Use hook to get dynamic aspect ratio (defaults to 4:5 while loading)
	const { aspectRatio } = useImageWithAspect(bakedImage, 4 / 5)

	return (
		<div
			className="relative bg-white  w-[500px] rounded-xl p-4 shadow-lg shadow-black/2 mx-auto"
			style={{
				fontFamily: 'Segoe UI Historic, Segoe UI, Helvetica, Arial, sans-serif',
			}}
		>
			<div className="flex flex-col">
				{/* Header */}
				<div className="flex items-center gap-2">
					<div className="h-12 w-12 flex items-center justify-center">
						<Jobibot size={40} />
					</div>
					<div>
						<div className="font-medium text-grey-900">Jobilla</div>
						<div className="text-grey-600 text-sm">{t('overview.preview.sponsored')}</div>
					</div>
				</div>

				{/* Primary Text */}
				<div className="text-sm mt-4 text-grey-900 whitespace-pre-wrap">
					{primaryText}
				</div>

				{/* Baked Campaign Image */}
				<div
					className="mt-4 relative overflow-hidden bg-grey-100 transition-all duration-300"
					style={{ aspectRatio }}
				>
					{bakedImage
						? (
							<img
								src={bakedImage}
								alt={campaign.title}
								className="w-full h-full object-contain"
							/>
						)
						: (
							<div className="w-full h-full flex items-center justify-center text-grey-400">
								{t('overview.preview.noImage')}
							</div>
						)}
				</div>

				{/* Link Preview Footer */}
				<div className="grid grid-cols-[1fr_auto] gap-4 p-3 items-center bg-grey-100">
					<div className="flex flex-col">
						<div className="text-grey-600 text-sm">
							{reachOverride ? new URL(reachOverride.url).hostname.toUpperCase() : 'FORMS.JOBILLA.COM'}
						</div>
						<div className="font-medium text-grey-900">
							{headlineText}
						</div>
						<div className="text-grey-600 text-sm">
							{descriptionText}
						</div>
					</div>
					<div>
						<div className="text-grey-600 ring ring-grey-200 p-2.5 text-sm rounded">
							{metaCtaText}
						</div>
					</div>
				</div>
			</div>

			{isLoading && (
				<div className="absolute inset-0 bg-white rounded-xl flex items-center justify-center">
					<SubtleLoader />
				</div>
			)}
		</div>
	)
}

export type StoryAdPreviewProps = {
	campaign: CampaignModel
	storyAd?: ConformedCampaignStoryAd
	isLoading: boolean
}

export function StoryAdPreview({ campaign, isLoading }: StoryAdPreviewProps) {
	const { t } = useTranslation('common')
	// The baked story image from the campaign
	const bakedImage = campaign.storyImage || campaign.image

	// Use hook to get dynamic aspect ratio (defaults to 9:16 for story format)
	const { aspectRatio } = useImageWithAspect(bakedImage, 9 / 16)

	return (
		<div
			className="bg-grey-900 rounded-xl shadow-lg shadow-black/2 overflow-hidden relative transition-all duration-300"
			style={{
				width: '500px',
				aspectRatio,
			}}
		>
			{bakedImage
				? (
					<img
						src={bakedImage}
						alt={campaign.title}
						className="w-full h-full object-contain"
					/>
				)
				: (
					<div className="w-full h-full flex items-center justify-center text-grey-500">
						{t('overview.preview.noImage')}
					</div>
				)}
			<InstagramOverlay showTooltips={false} />
		</div>
	)
}
